import { CustomerApi } from 'api';
import { GenericWarningMarkIcon } from 'assets/icons';
import VerificationForm from 'features/customerPanel/accountVerification/component/verificationForm';
import { getVerificationStatus } from 'features/login/store/login.action';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ErrorCard from 'shared/components/dialog/errorCard';
import FormSuccessDialog from 'shared/components/dialog/FormSuccessDialog';
import FormModal from 'shared/components/form/formModal';
import { addNotification } from 'shared/components/toast';
import { ACC_VERIFICATION_STATUS, APP_URL, PERSONAL_ID, SectionNavigation } from 'shared/constants/common';
import { VerificationEvents } from 'shared/constants/datalayer';
import useSectionNavigation from 'shared/hooks/useSectionNavigation';
import { State } from 'shared/interface';
import { isLoggedin } from 'shared/util/utility';

const AccountVerification = () => {
	const { goToRootView, isViewActive, getViewParams } = useSectionNavigation();
	const { stepId } = getViewParams();
	const { t } = useTranslation();
	const NUMBER_OF_STEPS = 6;
	const [dialogType, setDialogType] = useState('');
	const [dialogContent, setDialogContent] = useState('');
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const step = parseInt(stepId!);
	const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState({
		isPEP: false,
		giifType: '1',
		nonDictionaryType: '',
		documentType: PERSONAL_ID,
		uploadFront: '',
		uploadBack: '',
		expirationDate: '',
		seriesNumber: '',
		street: '',
		buildingNumber: '',
		apartmentNumber: '',
		city: '',
		postalcode: '',
		bankAccountNumber: '',
	});

	const dispatch = useDispatch();

	const history = useHistory();

	const isLogin = isLoggedin();

	const isVerification = isViewActive(SectionNavigation.ACCOUNT_VERIFICATION, true);

	const verificationStatus = useSelector((state: State) => state.login?.accountDetail?.account?.verificationStatus);
	const redirectToLogin = () => {
		history.push(APP_URL.login, `${APP_URL.home}${history.location.hash}`);
	};
	const handleVerificationRedirect = () => {
		if (!isLogin) {
			redirectToLogin();
		} else if (verificationStatus) {
			switch (verificationStatus) {
				case ACC_VERIFICATION_STATUS.accepted:
					dialogType === '' && goToRootView();
					addNotification('success', t('verificationSuccessDescription'));
					break;
				default:
					return;
			}
		}
	};

	useEffect(() => {
		handleVerificationRedirect();
	}, [verificationStatus]);

	const onSubmit = async (data: any) => {
		try {
			setLoading(true);
			const formData = new FormData();

			formData.append(
				'customerData',
				JSON.stringify({
					address: `${data.street.trim()} ${data.buildingNumber.trim()}/${data.apartmentNumber.trim()}`,
					city: data.city,
					postalCode: data.postalcode,
					pepAccepted: data.isPEP ? data.isPEP : false,
					giifType: {
						type: parseInt(data.giifType),
						nonDictionaryType: data.nonDictionaryType,
					},
					bankAccountNumber: data.bankAccountNumber,
				}),
			);
			formData.append(
				'documentData',
				JSON.stringify({
					documentType: data.documentType,
					expirationDate: moment(data.expirationDate).format('YYYY-MM-DD'),
					serialNumber: data.seriesNumber,
					uploadType: 'DISK',
				}),
			);
			formData.append('file1', data.uploadFront);
			formData.append('file2', data.uploadBack);
			CustomerApi.verifyAccount(formData)
				.then((resp: any) => {
					setLoading(false);
					if (resp && resp.data && !resp.data.isError) {
						setDialogType('success');
						dispatch(getVerificationStatus());
						VerificationEvents.SUCCESS({ ver_type: 'manual' });
					} else {
						const respCode = resp.data.code;
						VerificationEvents.FAILED({ ver_type: 'manual', fail_reason: resp.data.message ? resp.data.message : respCode });
						if (respCode === 413) {
							setDialogType('error');
							setDialogContent(`${t('imageSizeExceededVerifiction')}`);
						} else if (respCode === 430) {
							setDialogType('error');
							setDialogContent(`${t('verificationSerialIDIssue')}`);
						} else if (respCode === 423) {
							setDialogType('error');
							setDialogContent(`${t('unsupportedFileVerification')}`);
						} else {
							setDialogType('error');
							setDialogContent(resp && resp.data && resp.data.message ? resp.data.message : `${t('genericErrorMessage')}`);
						}
					}
				})
				.catch((error: any) => {
					setDialogType('error');
					setDialogContent(`${t('genericErrorMessage')}`);
					setLoading(false);
				});
		} catch (err) {
			console.error('err', err);
			setLoading(false);
		}
	};

	const handleFinishVerificationProcess = () => {
		setDialogType('');
		setDialogContent('');
		goToRootView();
		setLoading(false);
	};

	return (
		<>
			<FormModal
				open={isVerification}
				close={() => {
					goToRootView();
				}}
			>
				<>
					{dialogType === 'success' ? (
						<FormSuccessDialog text={t('verificationAccountSuccessToast')} closeDialog={handleFinishVerificationProcess} />
					) : (
						<VerificationForm
							formData={formData}
							initialValues={formData}
							onSubmit={onSubmit}
							step={step}
							closeDialog={goToRootView}
							numberOfSteps={NUMBER_OF_STEPS}
							setFormData={setFormData}
							isDesktop={false}
							loading={loading}
						/>
					)}
				</>
			</FormModal>

			{dialogType === 'error' && (
				<ErrorCard iconTitle={dialogContent} onClose={() => setDialogType('')} icon={GenericWarningMarkIcon} isGenericTextColor />
			)}
		</>
	);
};

export default AccountVerification;
